<template>
  <div :class="classMessageType">
    <div :class="classWrapper">
      <div v-if="showRetryButton" class="message__action">
        <hcc-button-icon @click="forceRetry" class="message__action-button">
          <retry-icon class="message__action-icon" />
        </hcc-button-icon>
      </div>
      <div v-if="showUnreadMark" class="message__indicator">
        <div class="message__indicator-icon"></div>
      </div>
      <div v-if="!isIncoming && showHeader" class="message__header">
        <div class="message__author">{{ authorName }}</div>
      </div>
      <slot></slot>
    </div>
    <span v-if="showFooter" :title="formattedTimestamp" class="message__footer">
      <div>
        {{ timestamp }}
        <message-footer-icon
          v-if="!isIncoming"
          :created-at="this.message.createdAt"
          :delivered-at="this.message.deliveredAt"
          :errorCode="this.message.errorCode"
          :typeUser="this.message.typeUser"
          :read-at="this.message.readAt"
        />
      </div>
      <div v-if="hasError" class="message__footer-error">
        {{ errorMessage }}
      </div>
    </span>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import Codes from '@/errors/codes';
import HccButtonIcon from '@/components/shared/HccButtonIcon/index.vue';

export default {
  components: {
    HccButtonIcon,
    MessageFooterIcon: () => import('@/components/message/MessageFooterIcon.vue'),
    RetryIcon: () => import('@/assets/icons/retry.svg'),
  },
  props: {
    message: {
      required: true,
      type: Object,
    },
    hasNext: {
      default: false,
    },
    sameMinute: {
      default: false,
    },
  },
  computed: {
    ...mapGetters([
      'userInfo',
      'failedJobMessage',
    ]),
    classMessageType() {
      return [
        'message',
        {
          'message--in': this.isIncoming,
          'message--out': !this.isIncoming,
          'message--sys': this.isAutomatic,
          'message--has-next': this.hasNext,
        },
      ];
    },
    isIncoming() {
      return this.typeUser === 'Client';
    },
    isAutomatic() {
      return this.typeUser === 'UserSystem';
    },
    typeUser() {
      return this.message.typeUser;
    },
    user() {
      return this.message.user;
    },
    authorName() {
      return this.user.name ?? '';
    },
    timestamp() {
      if (this.message.createdAt) {
        return this.globalFormatTime(this.message.createdAt);
      }

      return '';
    },
    formattedTimestamp() {
      if (this.message.createdAt) {
        return this.globalFormatDatetime(this.message.createdAt);
      }

      return '';
    },
    conversationData() {
      return this.message.conversation ?? { agent: null, client: null };
    },
    agentId() {
      return this.conversationData.agent ?? '';
    },
    isTemplate() {
      return this.message.type === 'tText';
    },
    classWrapper() {
      return {
        message__template: this.showBubble && this.isTemplate,
        message__bubble: this.showBubble,
        'message__bubble-error': this.showBubble && this.hasError,
        message__default: !this.showBubble,
      };
    },
    showFooter() {
      return !this.sameMinute || !this.message.createdAt;
    },
    showBubble() {
      return ['text', 'image', 'video', 'document', 'location', 'tText'].includes(this.message.type);
    },
    showUnreadMark() {
      return this.isIncoming && this.message.readAt === null;
    },
    showHeader() {
      return this.agentId !== this.userInfo.id;
    },
    showRetryButton() {
      return !this.isIncoming && this.failedJob;
    },
    failedJob() {
      if (!this.message.createdAt) {
        return this.failedJobMessage(this.message.tempId ?? '') ?? null;
      }

      return null;
    },
    hasError() {
      return this.message.errorCode && this.message.typeUser === 'User';
    },
    errorMessage() {
      const error = Codes.find(({ code }) => code === Number(this.message.errorCode));
      if (error) {
        return error.message;
      }

      return null;
    },
  },
  methods: {
    ...mapMutations([
      'restartFailedMessageJob',
    ]),
    forceRetry() {
      this.restartFailedMessageJob(this.message.tempId ?? '');
    },
  },
};
</script>
